import React, { useState } from 'react';
import { importService } from '../../services/api';

const ImportData = () => {
    const [importType, setImportType] = useState('products');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState('');
    const [templateDownloading, setTemplateDownloading] = useState(false);

    const importTypes = [
        { id: 'products', name: 'Products', description: 'Import products and components' },
        { id: 'inventory', name: 'Inventory', description: 'Import inventory quantities and locations' },
        { id: 'customers', name: 'Customers', description: 'Import customer data' },
        { id: 'suppliers', name: 'Suppliers', description: 'Import supplier data' }
    ];

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];

        if (selectedFile) {
            // Check file type
            if (!selectedFile.name.endsWith('.csv') && !selectedFile.name.endsWith('.xlsx')) {
                setError('Please upload a CSV or Excel file.');
                setFile(null);
                return;
            }

            // Check file size (limit to 10MB)
            if (selectedFile.size > 10 * 1024 * 1024) {
                setError('File size exceeds 10MB limit.');
                setFile(null);
                return;
            }

            setFile(selectedFile);
            setError('');
        }
    };

    const handleImport = async () => {
        if (!file) {
            setError('Please select a file to import.');
            return;
        }

        setLoading(true);
        setError('');
        setResults(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            let response;

            switch (importType) {
                case 'products':
                    response = await importService.importProducts(formData);
                    break;
                case 'inventory':
                    response = await importService.importInventory(formData);
                    break;
                case 'customers':
                    response = await fetch('/api/import/customers', {
                        method: 'POST',
                        body: formData
                    }).then(res => res.json());
                    break;
                case 'suppliers':
                    response = await fetch('/api/import/suppliers', {
                        method: 'POST',
                        body: formData
                    }).then(res => res.json());
                    break;
                default:
                    throw new Error('Invalid import type');
            }

            setResults(response);

            // Reset file input
            if (response.success) {
                setFile(null);
                document.getElementById('fileInput').value = '';
            }
        } catch (error) {
            console.error(`Error importing ${importType}:`, error);
            setError(error.response?.data?.message || `Failed to import ${importType}. Please check your file and try again.`);
        } finally {
            setLoading(false);
        }
    };

    const downloadTemplate = async () => {
        setTemplateDownloading(true);

        try {
            const response = await fetch(`/api/import/template/${importType}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to download template');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `${importType}_template.xlsx`;

            document.body.appendChild(a);
            a.click();

            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading template:', error);
            setError('Failed to download template. Please try again.');
        } finally {
            setTemplateDownloading(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Import Data</h1>

            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">Import Data from File</h2>
                </div>
                <div className="p-6">
                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 p-4 border border-red-400 bg-red-50 rounded-md text-red-700">
                            {error}
                        </div>
                    )}

                    {/* Import Type Selection */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Import Type
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {importTypes.map(type => (
                                <div
                                    key={type.id}
                                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                                        importType === type.id
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                    }`}
                                    onClick={() => setImportType(type.id)}
                                >
                                    <h3 className="font-medium text-gray-900">{type.name}</h3>
                                    <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* File Upload */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Upload File
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                            <div className="space-y-1 text-center">
                                <svg
                                    className="mx-auto h-12 w-12 text-gray-400"
                                    stroke="currentColor"
                                    fill="none"
                                    viewBox="0 0 48 48"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <div className="flex text-sm text-gray-600">
                                    <label
                                        htmlFor="fileInput"
                                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                                    >
                                        <span>Upload a file</span>
                                        <input
                                            id="fileInput"
                                            type="file"
                                            className="sr-only"
                                            accept=".csv,.xlsx"
                                            onChange={handleFileChange}
                                            disabled={loading}
                                        />
                                    </label>
                                    <p className="pl-1">or drag and drop</p>
                                </div>
                                <p className="text-xs text-gray-500">CSV or Excel files up to 10MB</p>
                                {file && (
                                    <p className="text-sm text-green-600 mt-2">
                                        Selected file: {file.name}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Template Download */}
                    <div className="mb-6">
                        <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm font-medium text-gray-900">Need a template?</h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Download a template file with the required columns for {importType}.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    onClick={downloadTemplate}
                                    disabled={templateDownloading}
                                >
                                    {templateDownloading ? 'Downloading...' : 'Download Template'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Import Action */}
                    <div className="flex justify-end">
                        <button
                            type="button"
                            className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                loading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            onClick={handleImport}
                            disabled={loading || !file}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Importing...
                                </>
                            ) : (
                                 'Import Data'
                             )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Results Section */}
            {results && (
                <div className="mt-8 bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-800">Import Results</h2>
                    </div>
                    <div className="p-6">
                        <div className={`p-4 rounded-md ${results.success ? 'bg-green-50 border border-green-400' : 'bg-red-50 border border-red-400'}`}>
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    {results.success ? (
                                        <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                         <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                             <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                         </svg>
                                     )}
                                </div>
                                <div className="ml-3">
                                    <h3 className={`text-sm font-medium ${results.success ? 'text-green-800' : 'text-red-800'}`}>
                                        {results.success ? 'Import Successful' : 'Import Failed'}
                                    </h3>
                                    <div className={`mt-2 text-sm ${results.success ? 'text-green-700' : 'text-red-700'}`}>
                                        <p>{results.message}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Display detailed results if available */}
                        {results.details && (
                            <div className="mt-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Detailed Results</h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Row</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                                        </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                        {results.details.map((detail, index) => (
                                            <tr key={index} className={detail.status === 'error' ? 'bg-red-50' : ''}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{detail.row || index + 1}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                                detail.status === 'success'
                                ? 'bg-green-100 text-green-800'
                                : detail.status === 'warning'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                            }`}>
                              {detail.status}
                            </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{detail.message}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Summary stats if available */}
                        {results.stats && (
                            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-green-50 p-4 rounded-md border border-green-200">
                                    <h4 className="text-sm font-medium text-green-800">Imported</h4>
                                    <p className="mt-2 text-3xl font-bold text-green-600">{results.stats.imported || 0}</p>
                                </div>
                                <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
                                    <h4 className="text-sm font-medium text-yellow-800">Updated</h4>
                                    <p className="mt-2 text-3xl font-bold text-yellow-600">{results.stats.updated || 0}</p>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                                    <h4 className="text-sm font-medium text-gray-800">Skipped</h4>
                                    <p className="mt-2 text-3xl font-bold text-gray-600">{results.stats.skipped || 0}</p>
                                </div>
                            </div>
                        )}

                        {/* Action buttons */}
                        <div className="mt-6 flex justify-end">
                            <button
                                type="button"
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                onClick={() => setResults(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImportData;
